import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  CircularProgress,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import HistoryIcon from "@material-ui/icons/History";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import utils from "../../universal/utils";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  filterBar: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    alignItems: "center",
    flexWrap: "wrap",
  },
  addButton: {
    marginLeft: "auto",
  },
  tableContainer: {
    maxHeight: 640,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  dialogContent: {
    minWidth: 520,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  activeChip: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  disabledChip: {
    backgroundColor: "#f44336",
    color: "white",
  },
  expiredChip: {
    backgroundColor: "#ff9800",
    color: "white",
  },
  paidChip: {
    backgroundColor: "#2e7d32",
    color: "white",
  },
  initiatedChip: {
    backgroundColor: "#1976d2",
    color: "white",
  },
  failedChip: {
    backgroundColor: "#d32f2f",
    color: "white",
  },
  cancelledChip: {
    backgroundColor: "#ef6c00",
    color: "white",
  },
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  statBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  historyTable: {
    minWidth: 720,
  },
});

const basePath = utils.getEdgeBasePath();

const labels = {
  filters: {
    search: "חיפוש",
    status: "סטטוס",
    all: "הכל",
    action: "סנן",
  },
  table: {
    title: "קישור",
    amount: "סכום",
    status: "סטטוס",
    stats: "נתונים",
    expiresAt: "תוקף",
    actions: "פעולות",
  },
  dialog: {
    create: "יצירת קישור תשלום",
    edit: "עריכת קישור תשלום",
    history: "היסטוריית תשלומים",
  },
  fields: {
    title: "כותרת",
    description: "תיאור התשלום",
    amount: "סכום לתשלום (₪)",
    expiresAt: "תוקף (אופציונלי)",
    active: "קישור פעיל",
  },
  buttons: {
    add: "קישור חדש",
    save: "שמור",
    cancel: "ביטול",
    copy: "העתק קישור",
    history: "היסטוריה",
  },
  status: {
    active: "פעיל",
    disabled: "מושבת",
    expired: "פג תוקף",
    paid: "שולם",
    initiated: "בתהליך",
    failed: "נכשל",
    cancelled: "בוטל",
  },
};

function toDateTimeLocalValue(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const pad = (num) => String(num).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatDate(value) {
  if (!value) return "ללא";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "ללא";

  return date.toLocaleString("he-IL");
}

function formatAmount(amount) {
  return `₪${Number(amount || 0).toFixed(2)}`;
}

class PaymentLinksManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      links: [],
      loading: true,
      saving: false,
      search: "",
      statusFilter: "",
      dialogOpen: false,
      dialogMode: "create",
      currentLink: {
        _id: null,
        title: "",
        description: "",
        amount: "",
        expiresAt: "",
        status: "active",
      },
      historyDialogOpen: false,
      historyLoading: false,
      historyLink: null,
      historyPayments: [],
      snackbar: {
        open: false,
        message: "",
        severity: "success",
      },
    };
  }

  componentDidMount() {
    this.loadLinks();
  }

  getAdminHeaders = (includeContentType = false) => {
    const token =
      typeof window !== "undefined"
        ? window.localStorage.getItem("admin_token")
        : "";
    const headers = token ? { "x-auth-token": token } : {};

    if (includeContentType) {
      headers["Content-Type"] = "application/json";
    }

    return headers;
  };

  showSnackbar = (message, severity = "success") => {
    this.setState({
      snackbar: {
        open: true,
        message,
        severity,
      },
    });
  };

  closeSnackbar = () => {
    this.setState((prevState) => ({
      snackbar: {
        ...prevState.snackbar,
        open: false,
      },
    }));
  };

  handleUnauthorized = () => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.removeItem("admin_token");
      window.localStorage.removeItem("admin_user");
    } catch (error) {
      console.error("Failed clearing admin auth state:", error);
    }

    window.location.reload();
  };

  loadLinks = async () => {
    this.setState({ loading: true });

    try {
      const queryParams = new URLSearchParams();
      if (this.state.search) queryParams.append("search", this.state.search);
      if (this.state.statusFilter) {
        queryParams.append("status", this.state.statusFilter);
      }

      const response = await fetch(
        `${basePath}/api/v1/admin/payment-links?${queryParams.toString()}`,
        {
          headers: this.getAdminHeaders(),
        }
      );
      const data = await response.json();

      if (response.status === 401) {
        this.handleUnauthorized();
        return;
      }

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "שגיאה בטעינת קישורי התשלום");
      }

      this.setState({ links: data.links || [] });
    } catch (error) {
      console.error("Error loading payment links:", error);
      this.showSnackbar(error.message || "שגיאה בטעינת קישורי התשלום", "error");
    } finally {
      this.setState({ loading: false });
    }
  };

  handleSearchChange = (event) => {
    this.setState({ search: event.target.value });
  };

  handleStatusFilterChange = (event) => {
    this.setState({ statusFilter: event.target.value });
  };

  openCreateDialog = () => {
    this.setState({
      dialogOpen: true,
      dialogMode: "create",
      currentLink: {
        _id: null,
        title: "",
        description: "",
        amount: "",
        expiresAt: "",
        status: "active",
      },
    });
  };

  openEditDialog = (link) => {
    this.setState({
      dialogOpen: true,
      dialogMode: "edit",
      currentLink: {
        _id: link._id,
        title: link.title || "",
        description: link.description || "",
        amount: String(link.amount || ""),
        expiresAt: toDateTimeLocalValue(link.expiresAt),
        status: link.storedStatus || link.status || "active",
      },
    });
  };

  closeDialog = () => {
    this.setState({ dialogOpen: false, saving: false });
  };

  handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    this.setState((prevState) => ({
      currentLink: {
        ...prevState.currentLink,
        [field]: value,
      },
    }));
  };

  handleActiveToggle = (event) => {
    const checked = event.target.checked;
    this.setState((prevState) => ({
      currentLink: {
        ...prevState.currentLink,
        status: checked ? "active" : "disabled",
      },
    }));
  };

  handleSave = async () => {
    const { dialogMode, currentLink } = this.state;

    if (!currentLink.description.trim() || !currentLink.amount) {
      this.showSnackbar("נא למלא תיאור וסכום", "error");
      return;
    }

    this.setState({ saving: true });

    try {
      const payload = {
        title: currentLink.title,
        description: currentLink.description,
        amount: Number(currentLink.amount),
        expiresAt: currentLink.expiresAt || null,
        status: currentLink.status,
      };

      const url =
        dialogMode === "create"
          ? `${basePath}/api/v1/admin/payment-links`
          : `${basePath}/api/v1/admin/payment-links/${currentLink._id}`;

      const response = await fetch(url, {
        method: dialogMode === "create" ? "POST" : "PATCH",
        headers: this.getAdminHeaders(true),
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.status === 401) {
        this.handleUnauthorized();
        return;
      }

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "שגיאה בשמירת הקישור");
      }

      this.showSnackbar(
        dialogMode === "create"
          ? "קישור התשלום נוצר בהצלחה"
          : "קישור התשלום עודכן בהצלחה"
      );
      this.closeDialog();
      this.loadLinks();
    } catch (error) {
      console.error("Error saving payment link:", error);
      this.showSnackbar(error.message || "שגיאה בשמירת הקישור", "error");
      this.setState({ saving: false });
    }
  };

  toggleStatus = async (link) => {
    const nextStatus = link.status === "active" ? "disabled" : "active";

    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/payment-links/${link._id}`,
        {
          method: "PATCH",
          headers: this.getAdminHeaders(true),
          body: JSON.stringify({ status: nextStatus }),
        }
      );
      const data = await response.json();

      if (response.status === 401) {
        this.handleUnauthorized();
        return;
      }

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "שגיאה בעדכון הסטטוס");
      }

      this.showSnackbar(
        nextStatus === "active" ? "הקישור הופעל" : "הקישור הושבת"
      );
      this.loadLinks();
    } catch (error) {
      console.error("Error toggling status:", error);
      this.showSnackbar(error.message || "שגיאה בעדכון הסטטוס", "error");
    }
  };

  copyPublicLink = async (link) => {
    const url =
      typeof window !== "undefined"
        ? `${window.location.origin}/pay/${link.token}`
        : `/pay/${link.token}`;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      this.showSnackbar("הקישור הועתק ללוח");
    } catch (error) {
      console.error("Copy failed:", error);
      this.showSnackbar("לא ניתן היה להעתיק את הקישור", "error");
    }
  };

  openHistoryDialog = async (link) => {
    this.setState({
      historyDialogOpen: true,
      historyLoading: true,
      historyLink: link,
      historyPayments: [],
    });

    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/payment-links/${link._id}/payments`,
        {
          headers: this.getAdminHeaders(),
        }
      );
      const data = await response.json();

      if (response.status === 401) {
        this.handleUnauthorized();
        return;
      }

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "שגיאה בטעינת ההיסטוריה");
      }

      this.setState({
        historyPayments: data.payments || [],
      });
    } catch (error) {
      console.error("Error loading payment history:", error);
      this.showSnackbar(error.message || "שגיאה בטעינת ההיסטוריה", "error");
    } finally {
      this.setState({ historyLoading: false });
    }
  };

  closeHistoryDialog = () => {
    this.setState({
      historyDialogOpen: false,
      historyLoading: false,
      historyLink: null,
      historyPayments: [],
    });
  };

  renderStatusChip = (status) => {
    const config = {
      active: {
        label: labels.status.active,
        className: this.props.classes.activeChip,
      },
      disabled: {
        label: labels.status.disabled,
        className: this.props.classes.disabledChip,
      },
      expired: {
        label: labels.status.expired,
        className: this.props.classes.expiredChip,
      },
      paid: {
        label: labels.status.paid,
        className: this.props.classes.paidChip,
      },
      initiated: {
        label: labels.status.initiated,
        className: this.props.classes.initiatedChip,
      },
      failed: {
        label: labels.status.failed,
        className: this.props.classes.failedChip,
      },
      cancelled: {
        label: labels.status.cancelled,
        className: this.props.classes.cancelledChip,
      },
    };
    const item = config[status] || config.disabled;

    return (
      <Chip
        size="small"
        label={item.label}
        className={item.className}
      />
    );
  };

  render() {
    const { classes } = this.props;
    const {
      links,
      loading,
      dialogOpen,
      dialogMode,
      currentLink,
      saving,
      historyDialogOpen,
      historyLoading,
      historyLink,
      historyPayments,
      snackbar,
      search,
      statusFilter,
    } = this.state;

    return (
      <div className={classes.root}>
        <div className={classes.filterBar}>
          <TextField
            label={labels.filters.search}
            value={search}
            onChange={this.handleSearchChange}
            variant="outlined"
            size="small"
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
            <InputLabel>{labels.filters.status}</InputLabel>
            <Select
              value={statusFilter}
              onChange={this.handleStatusFilterChange}
              label={labels.filters.status}
            >
              <MenuItem value="">{labels.filters.all}</MenuItem>
              <MenuItem value="active">{labels.status.active}</MenuItem>
              <MenuItem value="disabled">{labels.status.disabled}</MenuItem>
              <MenuItem value="expired">{labels.status.expired}</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={this.loadLinks}
          >
            {labels.filters.action}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={this.openCreateDialog}
            className={classes.addButton}
          >
            {labels.buttons.add}
          </Button>
        </div>

        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{labels.table.title}</TableCell>
                  <TableCell>{labels.table.amount}</TableCell>
                  <TableCell>{labels.table.status}</TableCell>
                  <TableCell>{labels.table.stats}</TableCell>
                  <TableCell>{labels.table.expiresAt}</TableCell>
                  <TableCell>{labels.table.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {links.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography className={classes.secondaryText}>
                        לא נמצאו קישורי תשלום
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  links.map((link) => (
                    <TableRow key={link._id}>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {link.title || "ללא כותרת"}
                        </Typography>
                        <Typography variant="body2" className={classes.secondaryText}>
                          {link.description}
                        </Typography>
                        <Typography variant="caption" className={classes.secondaryText}>
                          token: {link.token}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatAmount(link.amount)}</TableCell>
                      <TableCell>{this.renderStatusChip(link.status)}</TableCell>
                      <TableCell>
                        <div className={classes.statBlock}>
                          <Typography variant="body2">
                            תשלומים: <strong>{link.successfulPaymentsCount || 0}</strong>
                          </Typography>
                          <Typography variant="body2">
                            נאסף: <strong>{formatAmount(link.totalCollected)}</strong>
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(link.expiresAt)}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => this.copyPublicLink(link)}
                          title={labels.buttons.copy}
                        >
                          <FileCopyIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => this.openHistoryDialog(link)}
                          title={labels.buttons.history}
                        >
                          <HistoryIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => this.openEditDialog(link)}
                          title="ערוך"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => this.toggleStatus(link)}
                          title={link.status === "active" ? "השבת" : "הפעל"}
                        >
                          {link.status === "active" ? (
                            <PauseCircleOutlineIcon />
                          ) : (
                            <PlayArrowIcon />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={dialogOpen} onClose={this.closeDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {dialogMode === "create" ? labels.dialog.create : labels.dialog.edit}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <TextField
              label={labels.fields.title}
              value={currentLink.title}
              onChange={this.handleFieldChange("title")}
              fullWidth
              className={classes.formField}
            />
            <TextField
              label={labels.fields.description}
              value={currentLink.description}
              onChange={this.handleFieldChange("description")}
              fullWidth
              multiline
              rows={4}
              className={classes.formField}
            />
            <TextField
              label={labels.fields.amount}
              value={currentLink.amount}
              onChange={this.handleFieldChange("amount")}
              type="number"
              fullWidth
              className={classes.formField}
            />
            <TextField
              label={labels.fields.expiresAt}
              value={currentLink.expiresAt}
              onChange={this.handleFieldChange("expiresAt")}
              type="datetime-local"
              fullWidth
              className={classes.formField}
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={currentLink.status === "active"}
                  onChange={this.handleActiveToggle}
                  color="primary"
                />
              }
              label={labels.fields.active}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDialog}>{labels.buttons.cancel}</Button>
            <Button
              onClick={this.handleSave}
              color="primary"
              variant="contained"
              disabled={saving}
            >
              {saving ? "שומר..." : labels.buttons.save}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={historyDialogOpen}
          onClose={this.closeHistoryDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {labels.dialog.history}
            {historyLink ? ` - ${historyLink.title || historyLink.token}` : ""}
          </DialogTitle>
          <DialogContent>
            {historyLoading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress />
              </div>
            ) : (
              <TableContainer component={Paper}>
                <Table className={classes.historyTable}>
                  <TableHead>
                    <TableRow>
                      <TableCell>אסמכתא</TableCell>
                      <TableCell>סטטוס</TableCell>
                      <TableCell>משלם</TableCell>
                      <TableCell>סכום</TableCell>
                      <TableCell>שולם ב</TableCell>
                      <TableCell>אימיילים</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyPayments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography className={classes.secondaryText}>
                            אין עדיין תשלומים עבור הקישור הזה
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      historyPayments.map((payment) => (
                        <TableRow key={payment._id}>
                          <TableCell>{payment.paymentReference}</TableCell>
                          <TableCell>{this.renderStatusChip(payment.status)}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {payment.payer?.name || "ללא שם"}
                            </Typography>
                            <Typography
                              variant="caption"
                              className={classes.secondaryText}
                              display="block"
                            >
                              {payment.payer?.email || "-"}
                            </Typography>
                            <Typography
                              variant="caption"
                              className={classes.secondaryText}
                              display="block"
                            >
                              {payment.payer?.phone || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatAmount(payment.amount)}</TableCell>
                          <TableCell>{formatDate(payment.paidAt || payment.createdAt)}</TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">
                              לקוח: {payment.emailSentToCustomerAt ? "נשלח" : "לא נשלח"}
                            </Typography>
                            <Typography variant="caption" display="block">
                              הנהלה: {payment.emailSentToAdminsAt ? "נשלח" : "לא נשלח"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeHistoryDialog}>{labels.buttons.cancel}</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={this.closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={this.closeSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

PaymentLinksManager.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(PaymentLinksManager);
